import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputAdornment, Menu, MenuItem, OutlinedInput, Snackbar, TableCell, TableRow } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import CustomPagination from '../reusable/CustomPagination';
import Loader from '../reusable/Loader';
import Panel from '../reusable/Panel';
import Tag from '../reusable/Tag';
import CustomAlert from '../reusable/CustomAlert';
import {
  getUsers,
  resendUserInvite,
  resetUserMfa,
  resetUserPassword,
  updateUserStatus,
} from '../../services/userManagementService';
import { UserListItem } from '../../interfaces/UserManagementInterface';
import { USERS_PER_PAGE } from '../../constants';
import { formatRoleLabel, getErrorDetail, getUserRowActions, getUserStatusDisplay } from './userManagementUtils';
import UserFormModal from './UserFormModal';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './UserManagementLayout.css';

type ToastState = { open: boolean; message: string; severity: 'success' | 'error' };

const tableHeaders: TableHeaderData<UserListItem>[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role_label', label: 'Role' },
  { key: 'status', label: 'Status' },
  { key: 'eoa_cohort', label: 'EOA Cohort' },
  { key: 'actions', label: 'Actions' },
];

const UsersListPage = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'success' });
  const [editUser, setEditUser] = useState<UserListItem | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchUsers = useCallback(async () => {
    if (!userDetails) return;

    try {
      setLoading(true);
      const response = await getUsers(userDetails.access_token, {
        search: debouncedSearch || undefined,
        page: currentPage,
        size: USERS_PER_PAGE,
      });
      setUsers(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(response.data.pages);
    } catch (error: unknown) {
      console.error('Error fetching users:', error);
      handleOpenErrorModal(getErrorDetail(error, 'Failed to fetch users. Please try again.'), error as Error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, debouncedSearch, currentPage, handleOpenErrorModal]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: UserListItem) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleModifyUser = () => {
    setEditUser(selectedUser);
    setEditModalOpen(true);
    handleCloseMenu();
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditUser(null);
  };

  const handleUserSaved = (message: string) => {
    handleCloseEditModal();
    fetchUsers();
    setToast({ open: true, message, severity: 'success' });
  };

  const runUserAction = async (
    action: (token: string, user: UserListItem) => Promise<unknown>,
    getSuccessMessage: (user: UserListItem) => string,
    errorMessage: string
  ) => {
    if (!userDetails || !selectedUser) return;

    try {
      setActionInProgress(true);
      await action(userDetails.access_token, selectedUser);
      handleCloseMenu();
      setToast({ open: true, message: getSuccessMessage(selectedUser), severity: 'success' });
    } catch (error: unknown) {
      console.error(errorMessage, error);
      handleOpenErrorModal(getErrorDetail(error, errorMessage), error as Error);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleStatusChange = (status: 'ACTIVE' | 'INACTIVE') =>
    runUserAction(
      async (token, user) => {
        await updateUserStatus(token, user.id, { status });
        await fetchUsers();
      },
      (user) => {
        const userName = user.name || user.email;
        return status === 'INACTIVE' ? `${userName} has been deactivated.` : `${userName} has been activated.`;
      },
      'Failed to update user status. Please try again.'
    );

  const handleResendInvite = () =>
    runUserAction(
      (token, user) => resendUserInvite(token, user.id),
      (user) => `Invitation email sent to ${user.email}.`,
      'Failed to resend invite. Please try again.'
    );

  const handleResetPassword = () =>
    runUserAction(
      (token, user) => resetUserPassword(token, user.id),
      (user) => `Password reset email sent to ${user.email}.`,
      'Failed to reset password. Please try again.'
    );

  const handleResetMfa = () =>
    runUserAction(
      (token, user) => resetUserMfa(token, user.id),
      (user) => `MFA reset for ${user.name || user.email}.`,
      'Failed to reset MFA. Please try again.'
    );

  const selectedUserActions = selectedUser ? getUserRowActions(selectedUser) : null;

  if (!userDetails) return <Loader />;

  return (
    <div className="main-content user-management-page">
      <CustomButton
        className="user-management-back-button"
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate(-1)}
      />
      <div className="user-management-header">
        <h1 className="user-management-page-title">User Management</h1>
        <p className="user-management-description">Manage user accounts and permissions</p>
      </div>

      <div className="user-management-toolbar">
        <OutlinedInput
          className="user-management-search"
          placeholder="Search users..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          size="small"
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
        />
        <CustomButton
          variant="primary"
          buttonText="Add User"
          icon={<AddIcon />}
          iconPosition="start"
          buttonAction={() => {
            setEditUser(null);
            setEditModalOpen(true);
          }}
        />
      </div>

      {loading ? (
        <Loader />
      ) : users.length === 0 ? (
        <Panel className="user-management-panel user-management-table-panel">
          <p className="user-management-empty-state">No users found.</p>
        </Panel>
      ) : (
        <>
          <Panel className="user-management-panel user-management-table-panel">
            <CustomTable tableHeaderData={tableHeaders}>
              {users.map((user) => {
                const statusDisplay = getUserStatusDisplay(user);
                return (
                  <TableRow key={user.id}>
                    <TableCell className="user-management-name-cell">{user.name}</TableCell>
                    <TableCell className="user-management-email-cell">{user.email}</TableCell>
                    <TableCell>{formatRoleLabel(user.role_label)}</TableCell>
                    <TableCell>
                      <Tag type={statusDisplay.tagType} text={statusDisplay.label} size="small" />
                    </TableCell>
                    <TableCell>{user.eoa_cohort ?? '—'}</TableCell>
                    <TableCell align="right">
                      <button
                        type="button"
                        className="user-management-edit-action"
                        onClick={(event) => handleOpenMenu(event, user)}
                      >
                        Edit
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </CustomTable>
          </Panel>
          {totalPages > 1 && (
            <CustomPagination
              currentPage={currentPage}
              pages={totalPages}
              totalItems={totalItems}
              pageSize={USERS_PER_PAGE}
              handlePageChange={setCurrentPage}
            />
          )}
        </>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem disabled={actionInProgress} onClick={handleModifyUser}>
          Modify user information
        </MenuItem>
        {selectedUserActions?.canResendInvite && (
          <MenuItem disabled={actionInProgress} onClick={handleResendInvite}>
            Re-send invite email
          </MenuItem>
        )}
        {selectedUserActions?.canResetPassword && (
          <MenuItem disabled={actionInProgress} onClick={handleResetPassword}>
            Send password reset email
          </MenuItem>
        )}
        {selectedUserActions?.canResetMfa && (
          <MenuItem disabled={actionInProgress} onClick={handleResetMfa}>
            Reset email MFA
          </MenuItem>
        )}
        {selectedUserActions?.canDeactivate && (
          <MenuItem
            disabled={actionInProgress}
            className="user-management-menu-item-danger"
            onClick={() => handleStatusChange('INACTIVE')}
          >
            Deactivate User
          </MenuItem>
        )}
        {selectedUserActions?.canActivate && (
          <MenuItem disabled={actionInProgress} onClick={() => handleStatusChange('ACTIVE')}>
            Activate User
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <div>
          <CustomAlert alertType={toast.severity} header={toast.message} />
        </div>
      </Snackbar>

      <UserFormModal user={editUser} isOpen={editModalOpen} onClose={handleCloseEditModal} onSaved={handleUserSaved} />
    </div>
  );
};

export default UsersListPage;
